import { useState, useMemo, useEffect } from 'react';
import { Search, ArrowLeft, X, BookOpen, ChevronRight } from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from './ui/sheet';
import { Input } from './ui/input';
import { Button } from './ui/button';
import {
  HELP_CATEGORIES, HELP_ARTICLES,
  findArticle, articlesByCategory, searchArticles,
} from '../help_content';

/**
 * Slide-out help drawer with searchable knowledge base.
 *
 * Triggered by the floating ? button in the app shell. Internally it has
 * 2 views: index (sidebar of categories + articles) and article detail.
 */
export default function HelpDrawer({ open, onClose, initialArticleId }) {
  const [activeId, setActiveId] = useState(initialArticleId || null);
  const [query, setQuery] = useState('');

  // When the drawer is opened with a specific article id (deep-link), jump to it.
  useEffect(() => {
    if (open && initialArticleId) {
      setActiveId(initialArticleId);
    }
  }, [open, initialArticleId]);

  const matches = useMemo(() => searchArticles(query), [query]);
  const article = activeId ? findArticle(activeId) : null;

  // Render markdown-ish bold + headings inline. Keeps copy lightweight.
  const renderBody = (body) =>
    body.split(/\n\n+/).map((para, i) => (
      <p key={i} className="text-sm leading-relaxed text-foreground/90 mb-3 whitespace-pre-wrap">
        {para.split(/(\*\*[^*]+\*\*)/g).map((seg, j) =>
          seg.startsWith('**') && seg.endsWith('**') ? (
            <strong key={j} className="font-semibold text-foreground">{seg.slice(2, -2)}</strong>
          ) : (
            <span key={j}>{seg}</span>
          )
        )}
      </p>
    ));

  return (
    <Sheet
      open={open}
      onOpenChange={(o) => {
        if (!o) {
          setActiveId(null);
          setQuery('');
          onClose?.();
        }
      }}
    >
      <SheetContent
        side="right"
        className="w-full sm:max-w-lg overflow-y-auto p-0"
        data-testid="help-drawer"
      >
        {!article ? (
          <>
            <SheetHeader className="p-5 pb-3 border-b sticky top-0 bg-background z-10">
              <SheetTitle className="font-display text-2xl flex items-center gap-2">
                <BookOpen className="w-5 h-5 text-primary" />
                Help & Guides
              </SheetTitle>
              <p className="text-xs text-muted-foreground">
                {HELP_ARTICLES.length} articles · search or browse by topic
              </p>
              <div className="relative mt-2">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search help…"
                  className="pl-9"
                  data-testid="help-search-input"
                />
              </div>
            </SheetHeader>
            <div className="p-4 space-y-5" data-testid="help-index">
              {query.trim() ? (
                <div>
                  <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-2">
                    {matches.length} match{matches.length !== 1 ? 'es' : ''}
                  </p>
                  {matches.length === 0 ? (
                    <p className="text-sm text-muted-foreground py-3">
                      Nothing matches "{query}". Try a simpler keyword like "fine", "fund", or "reconcile".
                    </p>
                  ) : (
                    matches.map((a) => (
                      <ArticleRow key={a.id} article={a} onClick={() => setActiveId(a.id)} />
                    ))
                  )}
                </div>
              ) : (
                HELP_CATEGORIES.map((cat) => {
                  const arts = articlesByCategory(cat.id);
                  if (arts.length === 0) return null;
                  return (
                    <div key={cat.id} data-testid={`help-cat-${cat.id}`}>
                      <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-2">
                        {cat.label}
                      </p>
                      {arts.map((a) => (
                        <ArticleRow
                          key={a.id}
                          article={a}
                          onClick={() => setActiveId(a.id)}
                        />
                      ))}
                    </div>
                  );
                })
              )}
            </div>
          </>
        ) : (
          <ArticleView
            article={article}
            onBack={() => setActiveId(null)}
            onJump={setActiveId}
            renderBody={renderBody}
          />
        )}
      </SheetContent>
    </Sheet>
  );
}

function ArticleRow({ article, onClick }) {
  const Icon = article.icon;
  return (
    <button
      onClick={onClick}
      data-testid={`help-article-${article.id}`}
      className="w-full text-left flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-primary/5 transition group"
    >
      <span className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
        <Icon className="w-4 h-4 text-primary" />
      </span>
      <span className="text-sm font-medium flex-1 truncate">{article.title}</span>
      <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-primary transition" />
    </button>
  );
}

function ArticleView({ article, onBack, onJump, renderBody }) {
  const Icon = article.icon;
  return (
    <div data-testid={`help-article-view-${article.id}`}>
      <SheetHeader className="p-5 pb-3 border-b sticky top-0 bg-background z-10">
        <Button
          variant="ghost"
          size="sm"
          onClick={onBack}
          data-testid="help-article-back"
          className="self-start text-muted-foreground -ml-2 mb-1"
        >
          <ArrowLeft className="w-4 h-4 mr-1" /> back to index
        </Button>
        <SheetTitle className="font-display text-xl flex items-center gap-2">
          <span className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center">
            <Icon className="w-5 h-5 text-primary" />
          </span>
          {article.title}
        </SheetTitle>
      </SheetHeader>
      <div className="p-5 pt-3 space-y-4">
        {article.sections.map((s, i) => (
          <section key={i}>
            {s.heading && (
              <h3 className="font-semibold text-foreground mb-2">{s.heading}</h3>
            )}
            {renderBody(s.body)}
          </section>
        ))}
        {article.related && article.related.length > 0 && (
          <div className="pt-3 mt-3 border-t">
            <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-2">
              Related
            </p>
            {article.related.map((rid) => {
              const rel = findArticle(rid);
              if (!rel) return null;
              return (
                <ArticleRow
                  key={rid}
                  article={rel}
                  onClick={() => onJump(rid)}
                />
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
